import React, { useState } from "react";

type Tab = "overview" | "prechatSetup" | "prechat" | "askQuestion" | "setData" | "scopes" | "recipes" | "deployments";

const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "How It Works" },
  { key: "prechatSetup", label: "Prechat Setup" },
  { key: "prechat", label: "Prechat Data" },
  { key: "askQuestion", label: "Ask Question" },
  { key: "setData", label: "Set Data" },
  { key: "scopes", label: "Scopes" },
  { key: "recipes", label: "Recipes" },
  { key: "deployments", label: "Deployments" }
];

function CodeBlock({ children }: { children: string }) {
  return (
    <code className="font-mono text-sm text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
      {children}
    </code>
  );
}

function ExampleCard({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">{title}</h4>
      {children}
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex justify-center py-2">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="text-blue-400"
      >
        <path
          d="M12 5v14m0 0l-5-5m5 5l5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function FlowStep({
  step,
  title,
  description,
  color = "blue"
}: {
  step: number;
  title: string;
  description: React.ReactNode;
  color?: "blue" | "green" | "amber" | "purple";
}) {
  const colors = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    green: "bg-green-100 text-green-700 border-green-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200"
  };
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-full bg-white/60 flex items-center justify-center text-xs font-bold">
          {step}
        </span>
        <span className="font-semibold text-sm">{title}</span>
      </div>
      <div className="text-sm leading-relaxed">{description}</div>
    </div>
  );
}

const SCOPE_DISPLAY: Record<string, { label: string; color: string }> = {
  "Built-in": { label: "Built-in", color: "bg-slate-100 text-slate-600" },
  Contact:    { label: "Permanent", color: "bg-blue-100 text-blue-700" },
  Conversation: { label: "This Chat", color: "bg-amber-100 text-amber-700" },
  Workflow:   { label: "Step Only", color: "bg-green-100 text-green-700" }
};

function ScopeTag({ scope }: { scope: string }) {
  const def = SCOPE_DISPLAY[scope] ?? { label: scope, color: "bg-slate-100 text-slate-600" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${def.color}`}>
      {def.label}
    </span>
  );
}

function OverviewTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          What are variables?
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Variables are like fill-in-the-blanks for your workflows. Instead of
          writing the same message for everyone, you can use a placeholder like{" "}
          <CodeBlock>{"<<name>>"}</CodeBlock> and the system fills in each
          visitor&apos;s actual name automatically.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          Think of it like a mail merge in a word processor — you write the
          template once, and each person gets a personalized version.
        </p>
      </div>

      <ExampleCard title="Simple Example">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Visitor fills in prechat form"
            description={
              <span>
                Name: <strong>Sarah Johnson</strong> | Email:{" "}
                <strong>sarah@example.com</strong>
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='You write in "Send Message" node'
            description={
              <code className="text-sm font-mono">
                Hi {"<<name>>"}, thanks for contacting us!
              </code>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Visitor sees"
            description={
              <span className="font-medium">
                Hi Sarah Johnson, thanks for contacting us!
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Available Built-in Variables">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 pr-4 font-medium text-slate-500">
                  Variable
                </th>
                <th className="text-left py-2 pr-4 font-medium text-slate-500">
                  What it contains
                </th>
                <th className="text-left py-2 font-medium text-slate-500">
                  Where it comes from
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<name>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Full name</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<firstName>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">First name only</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<lastName>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Last name only</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<email>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Email address</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<phone>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">Phone number</td>
                <td className="py-2 text-slate-500">
                  Prechat form or contact record
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">
                  <CodeBlock>{"<<language>>"}</CodeBlock>
                </td>
                <td className="py-2 pr-4 text-slate-600">
                  Browser language code
                </td>
                <td className="py-2 text-slate-500">
                  Detected automatically (e.g. en, es, fr)
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ExampleCard>
    </div>
  );
}

function ConfigStep({
  step,
  title,
  children
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold mt-0.5">
        {step}
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-700 mb-2">{title}</div>
        {children}
      </div>
    </div>
  );
}

function NavHint({ to, label }: { to: string; label: string }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-slate-100 rounded-md px-2 py-1 text-xs text-slate-600">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        className="text-slate-400"
      >
        <path
          d="M9 5l7 7-7 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>
        Go to <strong>{to}</strong> &rarr; {label}
      </span>
    </div>
  );
}

function PrechatSetupTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Setting up prechat surveys
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          A prechat survey is the form visitors fill out before their chat
          starts. You deploy <strong>one script</strong> on your website and
          use <strong>Rules</strong> to control which survey shows up — the
          same script can show different forms based on conditions you set
          (page URL, time of day, language, country, etc.).
        </p>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 mt-3">
          <div className="text-sm text-blue-700">
            <strong>One script, one deploy.</strong> You paste one embed code on
            your website. Rules decide everything else — which survey to show,
            which chat window style to use, which workflow to run. No need to
            manage multiple scripts.
          </div>
        </div>
      </div>

      {/* Configuration Example 1: Default prechat */}
      <ExampleCard title="Example 1: One Default Prechat Survey for Everyone">
        <p className="text-sm text-slate-600 mb-4">
          The simplest setup — every visitor sees the same form before chatting.
          This is how most people start.
        </p>

        <div className="space-y-5">
          <ConfigStep step={1} title="Create your survey">
            <NavHint to="General Settings" label="Surveys" />
            <div className="mt-2 bg-slate-50 rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500 mb-2 font-medium">
                Click &quot;New Survey&quot;, name it &quot;Default
                Prechat&quot;, and add questions:
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                    Name
                  </span>
                  <span className="text-slate-600">
                    &quot;Your name&quot; — Required
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                    Email
                  </span>
                  <span className="text-slate-600">
                    &quot;Email address&quot; — Required
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-medium">
                    Choice
                  </span>
                  <span className="text-slate-600">
                    &quot;What do you need help with?&quot; — Options: Billing,
                    Technical, General
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Hit Save when done.
              </p>
            </div>
          </ConfigStep>

          <ConfigStep step={2} title="Create a rule to use this survey">
            <NavHint to="General Settings" label="Deployment &rarr; Rules tab" />
            <div className="mt-2 bg-slate-50 rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500 mb-2">
                Click &quot;New Rule&quot; and configure:
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">Trigger:</span>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                    Use Prechat Survey
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">
                    Conditions:
                  </span>
                  <span className="text-slate-600 italic">
                    None — leave empty so it applies to every visitor
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-20">Survey:</span>
                  <span className="text-slate-700 font-medium">
                    Select &quot;Default Prechat&quot;
                  </span>
                </div>
              </div>
            </div>
          </ConfigStep>

          <ConfigStep step={3} title="That's it! Variables are automatically available">
            <div className="mt-1 bg-green-50 rounded-lg border border-green-200 p-3">
              <p className="text-sm text-green-700">
                When a visitor fills out this form, the values automatically
                become variables in your workflow:
              </p>
              <div className="font-mono text-xs text-green-800 mt-2 space-y-0.5">
                <div>
                  {"<<name>>"} = whatever they typed in &quot;Your name&quot;
                </div>
                <div>
                  {"<<email>>"} = whatever they typed in &quot;Email
                  address&quot;
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Choice questions map to a variable if you set one in the
                survey&apos;s question settings (the &quot;Attribute&quot;
                column).
              </p>
            </div>
          </ConfigStep>
        </div>
      </ExampleCard>

      {/* Configuration Example 2: Different surveys by condition */}
      <ExampleCard title="Example 2: Different Surveys Based on Conditions">
        <p className="text-sm text-slate-600 mb-3">
          Same single embed code on your website, but you show different
          prechat forms depending on the situation. Use rules with conditions
          to control which survey appears.
        </p>
        <p className="text-sm text-slate-600 mb-4">
          For example: visitors on your pricing page get asked about company
          size and budget. Visitors on your help page get asked for an order
          number.
        </p>

        <div className="space-y-5">
          <ConfigStep step={1} title="Create a survey for each scenario">
            <NavHint to="General Settings" label="Surveys" />
            <div className="mt-2 space-y-3">
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                <div className="text-xs font-semibold text-blue-700 mb-2">
                  Survey: &quot;Sales Prechat&quot;
                </div>
                <div className="space-y-1 text-sm text-blue-600">
                  <div>Q1: Name (required)</div>
                  <div>Q2: Email (required)</div>
                  <div>
                    Q3: Company name (text, attribute: &quot;company&quot;)
                  </div>
                  <div>
                    Q4: Company size (choice: 1-10 / 11-50 / 50+, attribute:
                    &quot;companySize&quot;)
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                <div className="text-xs font-semibold text-amber-700 mb-2">
                  Survey: &quot;Support Prechat&quot;
                </div>
                <div className="space-y-1 text-sm text-amber-600">
                  <div>Q1: Name (required)</div>
                  <div>Q2: Email (required)</div>
                  <div>
                    Q3: Order number (text, attribute: &quot;orderNumber&quot;)
                  </div>
                  <div>
                    Q4: Issue type (choice: Billing / Technical / Shipping,
                    attribute: &quot;issueType&quot;)
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-semibold text-slate-600 mb-2">
                  Survey: &quot;Default Prechat&quot; (fallback for everyone
                  else)
                </div>
                <div className="space-y-1 text-sm text-slate-500">
                  <div>Q1: Name (required)</div>
                  <div>Q2: Email (required)</div>
                  <div>Q3: How can we help? (text)</div>
                </div>
              </div>
            </div>
          </ConfigStep>

          <ConfigStep step={2} title="Create rules with conditions">
            <NavHint to="General Settings" label="Deployment &rarr; Rules tab" />
            <p className="text-xs text-slate-500 mt-2 mb-3">
              Create multiple &quot;Use Prechat Survey&quot; rules. Rules are
              checked in priority order — the first one whose conditions match
              wins. Put your most specific rules first (priority 1, 2) and
              your default last.
            </p>
            <div className="mt-2 space-y-3">
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded text-xs font-bold">
                    Priority 1
                  </span>
                  <span className="text-xs font-semibold text-blue-700">
                    Sales pages get the Sales survey
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-blue-600">
                  <div>
                    Trigger:{" "}
                    <strong className="text-blue-800">
                      Use Prechat Survey
                    </strong>
                  </div>
                  <div>
                    Condition:{" "}
                    <span className="bg-white rounded px-1.5 py-0.5 border border-blue-200 text-xs font-mono">
                      URL contains &quot;/pricing&quot;
                    </span>
                  </div>
                  <div>
                    Survey:{" "}
                    <strong className="text-blue-800">
                      &quot;Sales Prechat&quot;
                    </strong>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-amber-200 text-amber-800 px-2 py-0.5 rounded text-xs font-bold">
                    Priority 2
                  </span>
                  <span className="text-xs font-semibold text-amber-700">
                    Help pages get the Support survey
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-amber-600">
                  <div>
                    Trigger:{" "}
                    <strong className="text-amber-800">
                      Use Prechat Survey
                    </strong>
                  </div>
                  <div>
                    Condition:{" "}
                    <span className="bg-white rounded px-1.5 py-0.5 border border-amber-200 text-xs font-mono">
                      URL contains &quot;/help&quot;
                    </span>
                  </div>
                  <div>
                    Survey:{" "}
                    <strong className="text-amber-800">
                      &quot;Support Prechat&quot;
                    </strong>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-xs font-bold">
                    Priority 3
                  </span>
                  <span className="text-xs font-semibold text-slate-600">
                    Everyone else gets the default
                  </span>
                </div>
                <div className="space-y-1.5 text-sm text-slate-500">
                  <div>
                    Trigger:{" "}
                    <strong className="text-slate-700">
                      Use Prechat Survey
                    </strong>
                  </div>
                  <div>
                    Condition:{" "}
                    <span className="italic">
                      None — no conditions means &quot;always matches&quot;
                    </span>
                  </div>
                  <div>
                    Survey:{" "}
                    <strong className="text-slate-700">
                      &quot;Default Prechat&quot;
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </ConfigStep>

          <ConfigStep step={3} title="Variables work the same regardless of which survey showed">
            <div className="mt-1 bg-green-50 rounded-lg border border-green-200 p-3">
              <p className="text-sm text-green-700 mb-2">
                No matter which survey the visitor got, the built-in variables
                are always the same:
              </p>
              <div className="font-mono text-xs text-green-800 space-y-0.5">
                <div>
                  {"<<name>>"} and {"<<email>>"} — always available from any
                  survey
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Custom attributes (like{" "}
                <CodeBlock>{"<<orderNumber>>"}</CodeBlock> or{" "}
                <CodeBlock>{"<<company>>"}</CodeBlock>) are only available if
                the visitor&apos;s survey had that question. Use{" "}
                <code className="bg-green-200 px-1 rounded font-mono">
                  {"?."}
                </code>{" "}
                in your workflow or check with a Condition node first.
              </p>
            </div>
          </ConfigStep>
        </div>
      </ExampleCard>

      {/* Available conditions */}
      <ExampleCard title="What Conditions Can You Use?">
        <p className="text-sm text-slate-600 mb-3">
          When creating a rule, you can add conditions to control when it
          applies. Here are all the conditions you can use:
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            {
              name: "URL",
              desc: "Page the visitor is on (contains /pricing, /help, etc.)"
            },
            {
              name: "Domain",
              desc: "Which website (support.acme.com vs www.acme.com)"
            },
            {
              name: "Country",
              desc: "Visitor's country (US, UK, CA, etc.)"
            },
            {
              name: "Language",
              desc: "Browser language (English, Spanish, French)"
            },
            {
              name: "Day of Week",
              desc: "Monday through Sunday"
            },
            {
              name: "Time of Day",
              desc: "Business hours vs after hours"
            },
            {
              name: "Channel",
              desc: "Web, SMS, WhatsApp, Facebook, etc."
            },
            {
              name: "Attribute",
              desc: "Any custom variable value"
            },
            {
              name: "Team",
              desc: "Which team is assigned"
            },
            {
              name: "Team Availability",
              desc: "Are agents online or offline?"
            }
          ].map((item) => (
            <div
              key={item.name}
              className="bg-slate-50 rounded border border-slate-200 p-2"
            >
              <div className="text-xs font-semibold text-slate-700">
                {item.name}
              </div>
              <div className="text-[11px] text-slate-500">{item.desc}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">
          You can combine multiple conditions with AND/OR logic. For example:
          &quot;URL contains /pricing AND country is US&quot;.
        </p>
      </ExampleCard>

      {/* How it all connects */}
      <ExampleCard title="How It All Connects">
        <p className="text-sm text-slate-600 mb-4">
          Here&apos;s the complete picture — one embed code, rules decide
          everything.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="You paste one embed script on your website"
            description={
              <span className="text-xs">
                Go to General Settings &rarr; Deployment &rarr; Deployment Code
                tab. Copy the script, paste it before{" "}
                <code className="bg-white/50 px-1 rounded">{"</body>"}</code> on
                every page where you want chat.
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="When a visitor arrives, rules are checked in priority order"
            description={
              <span className="text-xs">
                The system goes through your rules from priority 1, 2, 3... The
                first &quot;Use Prechat Survey&quot; rule whose conditions match
                determines which form to show.
              </span>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Visitor fills out the prechat form"
            description={
              <span className="text-xs">
                Their answers are saved as variables:{" "}
                <code className="bg-white/50 px-1 rounded">{"<<name>>"}</code>,{" "}
                <code className="bg-white/50 px-1 rounded">{"<<email>>"}</code>,
                plus any custom attributes you defined on the survey questions.
              </span>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Chat starts, your workflow has all the data"
            description={
              <span className="text-xs">
                All prechat variables are available immediately. Use them in Send
                Message, HTTP Request, Email, Set Data — anywhere you see the{" "}
                <code className="bg-white/50 px-1 rounded font-mono">
                  {"<< >>"}
                </code>{" "}
                button.
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      {/* Tips card */}
      <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
        <h4 className="text-sm font-semibold text-amber-800 mb-2">
          Tips for prechat surveys
        </h4>
        <ul className="text-sm text-amber-700 space-y-2 list-disc list-inside">
          <li>
            <strong>Keep it short</strong> — 2-4 questions max. Every extra
            question reduces the chance a visitor will start a chat.
          </li>
          <li>
            <strong>Always require name and email</strong> — these become{" "}
            <CodeBlock>{"<<name>>"}</CodeBlock> and{" "}
            <CodeBlock>{"<<email>>"}</CodeBlock>, which are the most useful
            variables for personalization.
          </li>
          <li>
            <strong>Use Choice questions for routing</strong> — if you ask
            &quot;What department?&quot; as a choice, you can use the answer in
            a Condition node to route to different workflow paths.
          </li>
          <li>
            <strong>Set attributes on questions</strong> — in the survey editor,
            the &quot;Attribute&quot; column maps the answer to a variable name.
            Set this to something like &quot;orderNumber&quot; so you can use{" "}
            <CodeBlock>{"<<orderNumber>>"}</CodeBlock> in your workflow.
          </li>
          <li>
            <strong>A rule with no conditions is your default</strong> — put
            it last (lowest priority). It catches everyone not matched by a
            more specific rule above it.
          </li>
          <li>
            <strong>Priority order matters</strong> — rules are checked from
            priority 1 down. Put your most specific rules first, your default
            last.
          </li>
        </ul>
      </div>
    </div>
  );
}

function PrechatTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          How prechat form data flows into your workflow
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          When a visitor fills out the prechat form before starting a chat, their
          answers are automatically saved as variables you can use anywhere in
          your workflow. You don&apos;t need to do anything special — it just works.
        </p>
      </div>

      <ExampleCard title="Complete Prechat Flow Example">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Visitor sees prechat form on your website"
            description={
              <div className="bg-white rounded border border-slate-300 p-3 mt-2 max-w-xs">
                <div className="text-xs text-slate-500 mb-2 font-medium">
                  Before we start, tell us about yourself:
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Name</div>
                    <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50">
                      Maria Garcia
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Email</div>
                    <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50">
                      maria@shopify-store.com
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Phone</div>
                    <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50">
                      (555) 123-4567
                    </div>
                  </div>
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="System automatically saves these variables"
            description={
              <div className="space-y-1 mt-1 font-mono text-xs">
                <div>
                  {"<<name>>"} = &quot;Maria Garcia&quot;
                </div>
                <div>
                  {"<<firstName>>"} = &quot;Maria&quot;
                </div>
                <div>
                  {"<<lastName>>"} = &quot;Garcia&quot;
                </div>
                <div>
                  {"<<email>>"} = &quot;maria@shopify-store.com&quot;
                </div>
                <div>
                  {"<<phone>>"} = &quot;(555) 123-4567&quot;
                </div>
              </div>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Your Send Message node uses them"
            description={
              <div className="mt-1">
                <div className="text-xs text-slate-500 mb-1">You type:</div>
                <code className="text-xs font-mono block bg-white/50 p-2 rounded">
                  Welcome {"<<firstName>>"}! I see you&apos;re reaching out from{" "}
                  {"<<email>>"}. How can I help?
                </code>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Maria sees the personalized message"
            description={
              <div className="mt-1 bg-white/50 p-2 rounded text-sm font-medium">
                Welcome Maria! I see you&apos;re reaching out from
                maria@shopify-store.com. How can I help?
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="What if the visitor skips a field?">
        <p className="text-sm text-slate-600 mb-3">
          If a visitor doesn&apos;t fill in a field (or your prechat form doesn&apos;t
          ask for it), the variable will be empty. Your message will still work —
          the placeholder will just be replaced with nothing.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="text-xs font-medium text-amber-700 mb-1">
            Tip: Use Ask Question first
          </div>
          <p className="text-xs text-amber-600">
            If you need a piece of information and aren&apos;t sure the prechat
            collected it, add an &quot;Ask Question&quot; node early in your workflow.
            Save the answer as a variable, and you can use it later.
          </p>
        </div>
      </ExampleCard>

      <ExampleCard title="Prechat + HTTP Request Example">
        <p className="text-sm text-slate-600 mb-3">
          A common pattern is using prechat data to look up a customer in your
          CRM or support system.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Prechat collects email"
            description={
              <span>
                <CodeBlock>{"<<email>>"}</CodeBlock> ={" "}
                <strong>maria@shopify-store.com</strong>
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="HTTP Request looks up the customer"
            description={
              <code className="text-xs font-mono">
                GET https://api.yourcrm.com/lookup?email={"<<email>>"}
              </code>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Send a personalized response"
            description={
              <code className="text-xs font-mono">
                Hi {"<<firstName>>"}, I found your account. How can I help?
              </code>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function AskQuestionTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Collecting answers with Ask Question
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          The &quot;Ask Question&quot; node lets you ask the visitor something and
          save their answer as a variable. You pick the variable name, and the
          visitor&apos;s response is stored there for use in later nodes.
        </p>
      </div>

      <ExampleCard title="Step-by-Step: Collecting an Order Number">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title='Configure your "Ask Question" node'
            description={
              <div className="mt-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24">Question:</span>
                  <span className="text-sm">
                    &quot;What is your order number?&quot;
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24">Type:</span>
                  <span className="text-sm">Text</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-24">Save as:</span>
                  <span className="text-sm font-mono bg-slate-100 px-1 rounded">
                    orderNumber
                  </span>
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="Visitor types their answer"
            description={
              <span>
                Visitor says: <strong>&quot;ORD-98765&quot;</strong>
              </span>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="System saves the answer"
            description={
              <span className="font-mono text-sm">
                {"<<orderNumber>>"} = &quot;ORD-98765&quot;
              </span>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Use it in a later Send Message"
            description={
              <div>
                <div className="text-xs text-slate-500 mb-1">Template:</div>
                <code className="text-xs font-mono">
                  Looking up order {"<<orderNumber>>"} for you now...
                </code>
                <div className="text-xs text-slate-500 mt-2 mb-1">
                  Visitor sees:
                </div>
                <span className="text-sm font-medium">
                  Looking up order ORD-98765 for you now...
                </span>
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Multiple Choice Example">
        <p className="text-sm text-slate-600 mb-3">
          Multiple choice questions let the visitor pick from options you
          define. Each option can lead to a different workflow path.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Configure the question"
            description={
              <div className="mt-1 space-y-2">
                <div>
                  <span className="text-xs text-slate-500">Question: </span>
                  <span className="text-sm">
                    &quot;What do you need help with?&quot;
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Type: </span>
                  <span className="text-sm">Multiple Choice</span>
                </div>
                <div className="text-xs text-slate-500">Options:</div>
                <div className="ml-2 space-y-1 text-sm">
                  <div className="bg-white/50 border rounded px-2 py-1">
                    Billing question
                  </div>
                  <div className="bg-white/50 border rounded px-2 py-1">
                    Technical support
                  </div>
                  <div className="bg-white/50 border rounded px-2 py-1">
                    General inquiry
                  </div>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Save as: </span>
                  <span className="font-mono text-sm bg-slate-100 px-1 rounded">
                    helpTopic
                  </span>
                </div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='Visitor clicks "Billing question"'
            description={
              <span className="font-mono text-sm">
                {"<<helpTopic>>"} = &quot;Billing question&quot;
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function SetDataTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Storing data with Set Data
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          The &quot;Set Data&quot; node lets you create or update variables without
          asking the visitor anything. It&apos;s useful for storing fixed values,
          combining existing variables, or preparing data for an API call.
        </p>
      </div>

      <ExampleCard title="Common Uses">
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Store a fixed value
            </div>
            <div className="font-mono text-xs text-slate-600 space-y-0.5">
              <div>
                Name: <strong>department</strong>
              </div>
              <div>
                Value: <strong>billing</strong>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Now you can use <CodeBlock>{"<<department>>"}</CodeBlock> anywhere
              = &quot;billing&quot;
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Build a message from variables
            </div>
            <div className="font-mono text-xs text-slate-600 space-y-0.5">
              <div>
                Name: <strong>summary</strong>
              </div>
              <div>
                Value:{" "}
                <strong>
                  {"<<firstName>>"} needs help with {"<<helpTopic>>"}
                </strong>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              <CodeBlock>{"<<summary>>"}</CodeBlock> ={" "}
              &quot;Maria needs help with Billing question&quot;
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Tag the conversation for agents
            </div>
            <div className="font-mono text-xs text-slate-600 space-y-0.5">
              <div>
                Name: <strong>priority</strong>
              </div>
              <div>
                Value: <strong>high</strong>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Save as <ScopeTag scope="Conversation" /> so agents can see it in
              the chat sidebar
            </div>
          </div>
        </div>
      </ExampleCard>

      <ExampleCard title="Real-World: Prepare Data for a CRM API">
        <p className="text-sm text-slate-600 mb-3">
          Use Set Data to organize information before sending it to an external
          system.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Prechat and Ask Question collect data"
            description={
              <div className="font-mono text-xs space-y-0.5 mt-1">
                <div>{"<<name>>"} = &quot;Maria Garcia&quot;</div>
                <div>{"<<email>>"} = &quot;maria@shopify-store.com&quot;</div>
                <div>{"<<orderNumber>>"} = &quot;ORD-98765&quot;</div>
                <div>{"<<helpTopic>>"} = &quot;Billing question&quot;</div>
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="Set Data builds a summary"
            description={
              <div className="font-mono text-xs space-y-0.5 mt-1">
                <div>
                  Name: <strong>ticketTitle</strong>
                </div>
                <div>
                  Value:{" "}
                  <strong>
                    {"<<helpTopic>>"} - Order {"<<orderNumber>>"}
                  </strong>
                </div>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="HTTP Request sends it to your CRM"
            description={
              <code className="text-xs font-mono block mt-1">
                {`POST https://api.yourcrm.com/tickets`}
                <br />
                {`{ "title": "<<ticketTitle>>", "email": "<<email>>" }`}
              </code>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function ScopesTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          How long does saved data last?
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          When you save a variable (using Ask Question or Set Data), you choose
          how long to keep it. There are three options — pick the shortest one
          that still gets the job done.
        </p>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
          <span className="font-semibold">Quick decision guide:</span>
          <ul className="mt-2 space-y-1">
            <li>→ Will they need this info remembered weeks from now? → <span className="font-medium">Permanent</span></li>
            <li>→ Only needed while they&apos;re chatting today? → <span className="font-medium">This Chat</span></li>
            <li>→ Just a temporary calculation inside this one workflow? → <span className="font-medium">Step Only</span></li>
          </ul>
        </div>
      </div>

      <div className="grid gap-4">
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScopeTag scope="Contact" />
            <span className="text-sm font-semibold text-blue-800">
              Remembered forever — even in future chats
            </span>
          </div>
          <p className="text-sm text-blue-700 mb-3">
            Like a customer profile. This data sticks with the person across all
            their chats, even months later.
          </p>
          <div className="bg-white rounded border border-blue-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">
              Good for:
            </div>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Email address, phone number, full name</li>
              <li>Customer tier (VIP, standard, enterprise)</li>
              <li>Language preference</li>
              <li>Account ID or membership number</li>
            </ul>
          </div>
          <div className="mt-2 bg-white rounded border border-blue-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Example:
            </div>
            <p className="text-sm text-slate-600">
              Maria chats today and gives her email. Two weeks later she chats
              again — <CodeBlock>{"<<email>>"}</CodeBlock> still has her email.
              No need to ask again.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScopeTag scope="Conversation" />
            <span className="text-sm font-semibold text-amber-800">
              Gone when this chat ends
            </span>
          </div>
          <p className="text-sm text-amber-700 mb-3">
            Like a sticky note for this conversation. All workflows in this chat
            can see it, but it&apos;s gone when the chat ends.
          </p>
          <div className="bg-white rounded border border-amber-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">
              Good for:
            </div>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Order number they&apos;re asking about</li>
              <li>What topic they need help with</li>
              <li>Ticket number created during the chat</li>
              <li>Issue category or department</li>
            </ul>
          </div>
          <div className="mt-2 bg-white rounded border border-amber-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Example:
            </div>
            <p className="text-sm text-slate-600">
              Maria says her order number is ORD-98765. You save it as{" "}
              <CodeBlock>{"<<orderNumber>>"}</CodeBlock> (This Chat).
              If the chat gets transferred to another workflow, that workflow can
              still see the order number — but once the chat closes, it&apos;s gone.
            </p>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <ScopeTag scope="Workflow" />
            <span className="text-sm font-semibold text-green-800">
              Gone the moment this workflow finishes
            </span>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Like scratch paper. Only this specific workflow can see it, and
            it&apos;s thrown away when the workflow finishes.
          </p>
          <div className="bg-white rounded border border-green-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-2">
              Good for:
            </div>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Temporary calculations or counters</li>
              <li>API response data you only need briefly</li>
              <li>Internal flags (&quot;hasVerifiedEmail&quot;, &quot;retryCount&quot;)</li>
              <li>Intermediate steps in multi-part processes</li>
            </ul>
          </div>
          <div className="mt-2 bg-white rounded border border-green-100 p-3">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Example:
            </div>
            <p className="text-sm text-slate-600">
              You call an API to check if a coupon code is valid. Save the
              result as <CodeBlock>{"<<couponValid>>"}</CodeBlock> (Step Only).
              You only need it for the next Condition node — it disappears the
              moment this workflow finishes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecipesTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          Ready-to-use workflow recipes
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed">
          Copy these patterns into your workflows. Each recipe shows the
          complete flow from start to finish.
        </p>
      </div>

      <ExampleCard title="Recipe: Customer Support Intake">
        <p className="text-sm text-slate-600 mb-3">
          Greet the visitor, find out what they need, and prepare the info for
          your agent team.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Send Message: Greet by name"
            description={
              <code className="text-xs font-mono">
                Hi {"<<firstName>>"}, welcome to our support chat! I&apos;m here to
                help.
              </code>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='Ask Question: "What do you need help with?"'
            description={
              <div className="text-xs space-y-0.5 mt-1">
                <div>Type: Multiple Choice</div>
                <div>Options: Billing | Technical | Account | Other</div>
                <div>
                  Save as:{" "}
                  <span className="font-mono bg-white/50 px-1 rounded">
                    helpTopic
                  </span>{" "}
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title='Ask Question: "Can you describe the issue?"'
            description={
              <div className="text-xs space-y-0.5 mt-1">
                <div>Type: Text</div>
                <div>
                  Save as:{" "}
                  <span className="font-mono bg-white/50 px-1 rounded">
                    issueDescription
                  </span>{" "}
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="Set Data: Build agent summary"
            description={
              <div className="font-mono text-xs mt-1 space-y-0.5">
                <div>
                  Name: <strong>agentNote</strong>
                </div>
                <div>
                  Value:{" "}
                  <strong>
                    {"<<name>>"} ({"<<email>>"}) — {"<<helpTopic>>"}:{" "}
                    {"<<issueDescription>>"}
                  </strong>
                </div>
                <div className="mt-1">
                  <ScopeTag scope="Conversation" />
                </div>
              </div>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={5}
            title="Transfer to Agents"
            description={
              <span className="text-xs">
                Agent sees:{" "}
                <span className="font-medium">
                  &quot;Maria Garcia (maria@shopify-store.com) — Billing: I was
                  charged twice for my last order&quot;
                </span>
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Recipe: Order Lookup with API">
        <p className="text-sm text-slate-600 mb-3">
          Ask for an order number, look it up in your system, and give the
          visitor a status update.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Ask Question: Get the order number"
            description={
              <div className="text-xs mt-1">
                Question: &quot;What&apos;s your order number? (e.g. ORD-12345)&quot;
                <br />
                Save as:{" "}
                <span className="font-mono bg-white/50 px-1 rounded">
                  orderId
                </span>{" "}
                <ScopeTag scope="Conversation" />
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="HTTP Request: Look up the order"
            description={
              <code className="text-xs font-mono block mt-1">
                GET https://api.yourstore.com/orders/{"<<orderId>>"}
              </code>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Send Message: Share the status"
            description={
              <code className="text-xs font-mono block mt-1">
                Your order {"<<orderId>>"} is currently: {"<<orderStatus>>"}.
                Expected delivery: {"<<deliveryDate>>"}.
              </code>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <ExampleCard title="Recipe: Email Follow-Up">
        <p className="text-sm text-slate-600 mb-3">
          Collect the issue, then send a confirmation email to the visitor with
          all the details.
        </p>
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Prechat provides email and name"
            description={
              <div className="font-mono text-xs mt-1">
                {"<<email>>"} = maria@shopify-store.com | {"<<name>>"} = Maria
                Garcia
              </div>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title='Ask Question: "Describe your issue"'
            description={
              <div className="text-xs mt-1">
                Save as:{" "}
                <span className="font-mono bg-white/50 px-1 rounded">
                  issueDetails
                </span>
              </div>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Send Email node"
            description={
              <div className="text-xs mt-1 space-y-1">
                <div>
                  To: <code className="font-mono">{"<<email>>"}</code>
                </div>
                <div>
                  Subject:{" "}
                  <code className="font-mono">
                    We received your request, {"<<firstName>>"}
                  </code>
                </div>
                <div>
                  Body:{" "}
                  <code className="font-mono">
                    Hi {"<<firstName>>"}, we&apos;ve received your message about:{" "}
                    {"<<issueDetails>>"}. A team member will get back to you
                    within 24 hours.
                  </code>
                </div>
              </div>
            }
            color="green"
          />
        </div>
      </ExampleCard>
    </div>
  );
}

function DeploymentsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">
          What is a Deployment?
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-3">
          A deployment is a separate chat entry point for your website or app.
          Each deployment gets its own embed code, default team routing, and chat
          window appearance. Think of deployments as different &quot;doors&quot;
          visitors can enter through.
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          All deployments share the same account, users, workflows, and rules.
          The difference is each one can be placed on a different page, brand,
          or department — each with its own look and routing.
        </p>
      </div>

      <ExampleCard title="Deployments vs Teams">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="text-sm font-semibold text-blue-800 mb-2">
              Deployments
            </div>
            <p className="text-sm text-blue-700 mb-3">
              Separate chat entry points — different embed codes for different
              sites, brands, or departments.
            </p>
            <ul className="text-sm text-blue-600 space-y-1 list-disc list-inside">
              <li>Each gets its own embed script</li>
              <li>Different chat window appearance</li>
              <li>Different default team routing</li>
              <li>Separate analytics (future)</li>
            </ul>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
            <div className="text-sm font-semibold text-amber-800 mb-2">
              Teams
            </div>
            <p className="text-sm text-amber-700 mb-3">
              Groups of agents within a single chat experience — used for
              routing and availability.
            </p>
            <ul className="text-sm text-amber-600 space-y-1 list-disc list-inside">
              <li>Route chats to specific agent groups</li>
              <li>Set availability per team</li>
              <li>Transfer between teams mid-chat</li>
              <li>Same embed code for all teams</li>
            </ul>
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 mt-4">
          <div className="text-xs font-medium text-slate-500 mb-1">
            Rule of thumb
          </div>
          <p className="text-sm text-slate-600">
            Use <strong>teams</strong> when you want different groups of agents
            handling chats from the <em>same</em> chat window. Use{" "}
            <strong>deployments</strong> when you need completely separate chat
            experiences (different websites, brands, or app sections).
          </p>
        </div>
      </ExampleCard>

      <ExampleCard title="When to Use Multiple Deployments">
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Multiple brands or websites
            </div>
            <p className="text-xs text-slate-500">
              Your company runs support.acme.com and shop.acme.com — each gets
              its own deployment with a different chat window style and default
              team.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Department-specific chat
            </div>
            <p className="text-xs text-slate-500">
              Your sales page has one chat widget (routed to Sales team) and your
              help center has another (routed to Support team). Different look,
              different greeting, different team.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-medium text-slate-700 mb-1">
              Embedded in different apps
            </div>
            <p className="text-xs text-slate-500">
              You embed chat in your main website, your mobile app webview, and
              your partner portal. Each gets its own deployment so you can track
              and configure them independently.
            </p>
          </div>
        </div>
      </ExampleCard>

      <ExampleCard title="How It Works">
        <div className="space-y-1">
          <FlowStep
            step={1}
            title="Create a deployment in Settings"
            description={
              <span className="text-xs">
                Go to General Settings &rarr; Deployment. Give it a name, pick a
                default team, and save.
              </span>
            }
            color="blue"
          />
          <FlowArrow />
          <FlowStep
            step={2}
            title="Copy the embed script"
            description={
              <span className="text-xs">
                Each deployment has its own unique embed code. Go to the
                &quot;Deployment Code&quot; tab to copy it.
              </span>
            }
            color="purple"
          />
          <FlowArrow />
          <FlowStep
            step={3}
            title="Paste it on the target page"
            description={
              <span className="text-xs">
                Add the script before{" "}
                <code className="bg-white/50 px-1 rounded">{"</body>"}</code> on
                the page where you want this chat to appear.
              </span>
            }
            color="amber"
          />
          <FlowArrow />
          <FlowStep
            step={4}
            title="All changes happen in the admin — no redeployment needed"
            description={
              <span className="text-xs">
                Change the team, chat window, or rules at any time in the admin
                UI. The embed script stays the same — your website doesn&apos;t need
                to be updated.
              </span>
            }
            color="green"
          />
        </div>
      </ExampleCard>

      <div className="bg-green-50 rounded-lg border border-green-200 p-4">
        <h4 className="text-sm font-semibold text-green-800 mb-2">
          Key points
        </h4>
        <ul className="text-sm text-green-700 space-y-2 list-disc list-inside">
          <li>
            <strong>One site ID, multiple embed scripts</strong> — all
            deployments share the same account and data.
          </li>
          <li>
            <strong>No redeployment needed</strong> — once the embed script is
            placed, all behavior changes (team, chat window, rules) happen in
            the admin UI.
          </li>
          <li>
            <strong>Shared workflows and rules</strong> — your workflows work
            across all deployments. Rule scoping per deployment is planned for a
            future release.
          </li>
          <li>
            <strong>Per-deployment reporting</strong> — each deployment tags its
            analytics data separately, so you can see metrics per entry point
            (coming soon).
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function VariableGuide() {
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">
          Variable Guide
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Learn how to use variables to personalize messages, collect data, and
          connect to external systems.
        </p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "overview" && <OverviewTab />}
      {activeTab === "prechatSetup" && <PrechatSetupTab />}
      {activeTab === "prechat" && <PrechatTab />}
      {activeTab === "askQuestion" && <AskQuestionTab />}
      {activeTab === "setData" && <SetDataTab />}
      {activeTab === "scopes" && <ScopesTab />}
      {activeTab === "recipes" && <RecipesTab />}
      {activeTab === "deployments" && <DeploymentsTab />}
    </div>
  );
}
